import React from 'react';
import {
  CheckCircle as PublishedIcon,
  ErrorOutline as FailedIcon,
  Block as RejectedIcon,
  Schedule as ScheduledIcon,
  Edit as DraftIcon,
  RemoveCircleOutline as PartialIcon,
  DeleteSweep as RemovedIcon,
} from '@mui/icons-material';
import { PostStatus, PostResultStatus } from '../types';

/**
 * How every post status looks, and what may be done to a post in it.
 *
 * This lives in one place because Post History, the Calendar and the Dashboard all render
 * status and all offer actions. When the rules were inline in Post History, the Calendar
 * showed hardcoded sample data and no other screen could act on a post at all.
 *
 * The `can*` flags mirror the server's rules rather than inventing UI-only ones — the
 * server is the boundary (see hasGoneOut() and validatePostTargets() in server/api.js).
 * Hiding a button the server would refuse is a courtesy; the refusal is the real guard.
 */

export type MuiColor = 'success' | 'error' | 'warning' | 'info' | 'default';

export interface StatusMeta {
  label: string;
  /** Longer sentence for the detail dialog, where there is room to explain. */
  description: string;
  color: MuiColor;
  icon: React.ReactElement;
  /** Publishing now would reach the platforms. False once anything has gone out. */
  canPublish: boolean;
  /** Content may still be changed. False once anything has gone out. */
  canEdit: boolean;
  /** A future time may be set. Same rule as editing. */
  canSchedule: boolean;
  /**
   * The post may be deleted. For a post that has gone out this can also take the live copy
   * down from the platforms — the dialog asks, rather than assuming either way.
   */
  canDelete: boolean;
}

// Anything that reached a platform stays immutable: the content is live somewhere, so editing
// would misrepresent it and re-publishing would post a duplicate.
//
// Deleting IS allowed, because the delete path can now take the live copy down from the
// platform rather than only hiding it here — which is what made the old "you must remove it
// on the platform itself" rule necessary. The confirmation dialog is where that choice is
// made; see DeletePostDialog.
const GONE_OUT = { canPublish: false, canEdit: false, canSchedule: false, canDelete: true };
// Nothing was sent, so the post is still entirely ours to change.
const NOT_SENT = { canPublish: true, canEdit: true, canSchedule: true, canDelete: true };

export const POST_STATUS: Record<PostStatus, StatusMeta> = {
  published: {
    label: 'Published',
    description: 'Every platform this post targeted accepted it.',
    color: 'success',
    icon: <PublishedIcon fontSize="small" />,
    ...GONE_OUT,
  },
  partial: {
    label: 'Partially published',
    description:
      'Some platforms accepted this post and others did not. It cannot be edited or ' +
      're-published, because that would duplicate it where it already went out.',
    color: 'warning',
    icon: <PartialIcon fontSize="small" />,
    ...GONE_OUT,
  },
  rejected: {
    label: 'Rejected',
    description:
      'Nothing was sent. ShoutVector refused every platform before contacting it — ' +
      'the reasons are listed per platform below, and fixing them makes this publishable.',
    color: 'warning',
    icon: <RejectedIcon fontSize="small" />,
    ...NOT_SENT,
  },
  failed: {
    label: 'Failed',
    description: 'Nothing went out. At least one platform was contacted and refused the post.',
    color: 'error',
    icon: <FailedIcon fontSize="small" />,
    ...NOT_SENT,
  },
  scheduled: {
    label: 'Scheduled',
    description: 'Waiting for its time. The scheduler publishes it within about a minute of falling due.',
    color: 'info',
    icon: <ScheduledIcon fontSize="small" />,
    ...NOT_SENT,
  },
  draft: {
    label: 'Draft',
    description: 'Saved but not scheduled or published.',
    color: 'default',
    icon: <DraftIcon fontSize="small" />,
    ...NOT_SENT,
  },
};

/** Never throws on an unknown status from an older row; falls back to Draft. */
export const statusMeta = (status: string): StatusMeta =>
  POST_STATUS[status as PostStatus] ?? POST_STATUS.draft;

/** Statuses offered as filters, in the order they are shown. */
export const FILTERABLE_STATUSES: PostStatus[] = [
  'published',
  'partial',
  'failed',
  'rejected',
  'scheduled',
  'draft',
];

// ---------------------------------------------------------------------------
// Per-platform result rows
// ---------------------------------------------------------------------------

export interface ResultMeta {
  label: string;
  color: MuiColor;
  icon: React.ReactElement;
}

export const RESULT_STATUS: Record<PostResultStatus, ResultMeta> = {
  success: { label: 'Published', color: 'success', icon: <PublishedIcon fontSize="small" /> },
  // Published, then taken down from the platform. Kept as its own outcome rather than
  // reverting to an unpublished state: it did go out, and the audit trail should say so.
  removed: { label: 'Removed', color: 'default', icon: <RemovedIcon fontSize="small" /> },
  // Distinct from failed on purpose: the user can act on a rejection, because the reason is
  // something on this side — too long, not connected, media missing.
  rejected: { label: 'Rejected', color: 'warning', icon: <RejectedIcon fontSize="small" /> },
  failed: { label: 'Failed', color: 'error', icon: <FailedIcon fontSize="small" /> },
};

export const resultMeta = (status?: string): ResultMeta | null =>
  status ? RESULT_STATUS[status as PostResultStatus] ?? RESULT_STATUS.failed : null;

/**
 * The server stores a rejection as "code: sentence" so the reason survives in one TEXT
 * column while staying machine-readable. Split it for display — the sentence is written to
 * be read on its own, and the code is only useful for branching.
 */
export const parseRejection = (message?: string): { code: string | null; text: string } => {
  if (!message) return { code: null, text: '' };
  const match = message.match(/^([a-z_]+):\s*(.+)$/s);
  return match ? { code: match[1], text: match[2] } : { code: null, text: message };
};

/**
 * What a platform's outcome should say in a tooltip or list. Kept here so History, the
 * dialog and the Calendar all phrase the same outcome identically.
 */
export const outcomeText = (
  platform: string,
  result?: { status: string; error_message?: string; platform_post_id?: string }
): string => {
  if (!result) return `${platform}: not published yet`;
  if (result.status === 'success') return `${platform}: published`;
  if (result.status === 'removed') {
    const { text } = parseRejection(result.error_message);
    return `${platform}: published, then ${text || 'removed from the platform'}`;
  }
  const { text } = parseRejection(result.error_message);
  const verb = result.status === 'rejected' ? 'not sent' : 'failed';
  return `${platform}: ${verb} — ${text || 'no reason recorded'}`;
};
